import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector } from '../hooks';

import { TombstoneActions } from '../actions/tombstone/tombstone.actions';

import type { Tombstone } from '../interfaces/client/tombstone.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import FilterChipStrip from './components/FilterChipStrip';
import SegmentedControl from './components/SegmentedControl';
import TombstoneCard from './components/TombstoneCard';
import EditPencil from './components/EditPencil';

import TombstoneEditModal from './TombstoneEditModal';
import CreateTombstoneModal from './CreateTombstoneModal';
import {
  num,
  formatZar,
  yearOf,
  formatDate,
  formatMonthYear,
  deriveTombstoneType,
  deriveTombstoneRole
} from './lib/mandate-helpers';
import { useTableSort, SortableTh } from './lib/use-table-sort';

// =============================================================================
//  Tombstones — v3 redesign
//
//  Available from /tombstones:    id, category (denormalised string),
//                                 sub_category, date, description, client,
//                                 client_id, size_of_transaction, fee_of_transaction
//  Stubbed deterministically:     "type" (M&A/Listing/Capital Raise/etc — derived
//                                 from category), BSM role (Sell-side advisor/etc),
//                                 sector (no direct join from Tombstone → sector)
// =============================================================================

const VIEW_OPTIONS = [
  { value: 'cards' as const, icon: 'cards' as const, ariaLabel: 'Cards' },
  { value: 'rows' as const, icon: 'rows' as const, ariaLabel: 'Rows' }
];

const YEAR_OPTIONS = ['All', '2026', '2025', '2024', '2023'] as const;
type YearKey = (typeof YEAR_OPTIONS)[number];

const TombstonesListV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const tombstones: Tombstone[] =
    useAppSelector(({ tombstone_store }) => tombstone_store.tombstones) || [];

  const [view, setView] = useState<'cards' | 'rows'>('cards');
  const [yearFilter, setYearFilter] = useState<YearKey>('All');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    dispatch(TombstoneActions.loadTombstones(noOp));
  }, [dispatch]);

  // Dispatch the existing download saga (TombstoneActions.DOWNLOAD_TOMBSTONES)
  // which calls /tombstones.csv, reads the Content-Disposition filename, and
  // triggers a browser download via an injected anchor.  Loading flag flips
  // the button label so the user sees the request is in flight.
  const handleExport = () => {
    if (exporting) return;
    setExporting(true);
    dispatch(TombstoneActions.downloadTombstones(() => setExporting(false)));
  };

  const aggregateValue = useMemo(
    () => tombstones.reduce((sum, t) => sum + num(t.size_of_transaction), 0),
    [tombstones]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tombstones.filter((t) => {
      const y = String(yearOf(t.date));
      if (yearFilter !== 'All' && y !== yearFilter) return false;
      if (q) {
        const hay =
          `${t.client ?? ''} ${t.category ?? ''} ${t.sub_category ?? ''} ${t.description ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tombstones, yearFilter, search]);

  // Most recent first by default; every column is now clickable too.
  const SORT_ACCESSORS = useMemo(
    () => ({
      year: (t: Tombstone) => (t.date ? new Date(t.date as string) : null),
      client: (t: Tombstone) => t.client,
      project: (t: Tombstone) => t.description,
      type: (t: Tombstone) => deriveTombstoneType(t),
      sector: (t: Tombstone) => t.sub_category || t.category,
      role: (t: Tombstone) => deriveTombstoneRole(t),
      value: (t: Tombstone) => num(t.size_of_transaction),
      date: (t: Tombstone) => (t.date ? new Date(t.date as string) : null)
    }),
    []
  );
  const { sorted, sort, toggle } = useTableSort(filtered, SORT_ACCESSORS, {
    key: 'date',
    dir: 'desc'
  });

  return (
    <>
      <Topbar
        crumbs={['Deal Flow', 'Tombstones']}
        tools={
          // Wires the existing DOWNLOAD_TOMBSTONES saga → /tombstones.csv.
          <button
            className="btn ghost"
            type="button"
            onClick={handleExport}
            disabled={exporting}
            title={exporting ? 'Preparing CSV…' : 'Download CSV of all tombstones'}
          >
            <Icon name="download" size={14} />
            {exporting ? 'Exporting…' : 'Export'}
          </button>
        }
      />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Track Record</div>
            <h1 className="page-title">Tombstones</h1>
            <div className="page-sub">
              {filtered.length === tombstones.length
                ? `${tombstones.length} closed transactions · ${formatZar(aggregateValue)} aggregate value`
                : `${filtered.length} of ${tombstones.length} · ${formatZar(aggregateValue)} aggregate value`}
            </div>
          </div>
          <div className="page-tools">
            <label className="search">
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder="Search transactions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <SegmentedControl<'cards' | 'rows'>
              options={VIEW_OPTIONS}
              value={view}
              onChange={setView}
            />
            {/* Export lives in the Topbar only, matching Mandates, Clients and
                Employees.  A second copy used to sit here as well. */}
            <button className="btn primary" type="button" onClick={() => setCreating(true)}>
              <Icon name="plus" size={14} />
              New tombstone
            </button>
          </div>
        </div>

        <FilterChipStrip<YearKey>
          options={YEAR_OPTIONS.map((y) => ({ value: y, label: y }))}
          value={yearFilter}
          onChange={setYearFilter}
        />

        {sorted.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <span className="muted">No tombstones match these filters.</span>
          </div>
        ) : view === 'cards' ? (
          <div className="grid cards-3">
            {sorted.map((t) => (
              <TombstoneCard
                key={t.id}
                year={yearOf(t.date)}
                type={deriveTombstoneType(t)}
                value={formatZar(t.size_of_transaction)}
                client={t.client || 'Unnamed'}
                project={t.description ? t.description.split('\n')[0].slice(0, 60) : undefined}
                role={deriveTombstoneRole(t)}
                sector={t.sub_category || t.category}
                when={formatMonthYear(t.date)}
                onClick={() => navigate(`/tombstones/${t.id}`)}
                onEdit={() => setEditingId(t.id as number | string)}
              />
            ))}
          </div>
        ) : (
          <div className="card flush">
            <table className="tbl">
              <thead>
                <tr>
                  <SortableTh label="Year" sortKey="year" sort={sort} onToggle={toggle} />
                  <SortableTh label="Client" sortKey="client" sort={sort} onToggle={toggle} />
                  <SortableTh label="Project" sortKey="project" sort={sort} onToggle={toggle} />
                  <SortableTh label="Type" sortKey="type" sort={sort} onToggle={toggle} />
                  <SortableTh label="Sector" sortKey="sector" sort={sort} onToggle={toggle} />
                  <SortableTh label="BSM role" sortKey="role" sort={sort} onToggle={toggle} />
                  <th className="right">Value</th>
                  <th>Date</th>
                  <th className="actions-cell" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/tombstones/${t.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="num">{yearOf(t.date)}</td>
                    <td className="strong">{t.client || '—'}</td>
                    <td>{t.description ? t.description.split('\n')[0].slice(0, 50) : '—'}</td>
                    <td>
                      <span className="chip gold">{deriveTombstoneType(t)}</span>
                    </td>
                    <td>
                      {t.sub_category ? <span className="chip muted">{t.sub_category}</span> : '—'}
                    </td>
                    <td>{deriveTombstoneRole(t) ?? <span className="muted">Not recorded</span>}</td>
                    <td className="num right strong">{formatZar(t.size_of_transaction)}</td>
                    <td className="mono">{formatDate(t.date)}</td>
                    <td className="actions-cell">
                      <EditPencil
                        onClick={() => setEditingId(t.id as number | string)}
                        ariaLabel={`Edit tombstone ${t.client || t.id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingId !== null &&
        (() => {
          const t = sorted.find((x) => String(x.id) === String(editingId));
          if (!t) return null;
          return <TombstoneEditModal tombstone={t} onClose={() => setEditingId(null)} />;
        })()}

      {creating && <CreateTombstoneModal onClose={() => setCreating(false)} />}
    </>
  );
};

export default TombstonesListV3;
